import NextLink from "next/link";
import type { StackProps } from "@chakra-ui/react";
import {
  Box,
  Center,
  Image as ChakraImage,
  HStack,
  Icon,
} from "@chakra-ui/react";
import { RiMoneyDollarCircleLine, RiStore2Line } from "react-icons/ri";
import type { ColorScheme, ColorSchemeType } from "data/color-schemes";
import colorSchemes, { isColorSchemeType } from "data/color-schemes";

const getColorAndIcon = (url: string, type?: ColorSchemeType) => {
  let color: ColorScheme = colorSchemes.company;
  let icon = RiStore2Line;

  if (type && colorSchemes[type]) {
    color = colorSchemes[type];
    if (type === "investor") {
      icon = RiMoneyDollarCircleLine;
    }
  } else if (url) {
    try {
      const pathStart = url.slice(1).split("/")[0].toLowerCase().trim();
      if (!!pathStart && isColorSchemeType(pathStart)) {
        color = colorSchemes[pathStart];

        if (pathStart === "investor") {
          icon = RiMoneyDollarCircleLine;
        }
      }
    } catch (err) {
      // console.error(err);
    }
  }

  return { color, icon };
};

const linkStyles: StackProps = {
  color: "blue.600",
  fontWeight: "bold",
  _hover: { textDecoration: "underline" },
  _focus: { boxShadow: "outline" },
  _active: { color: "blue.800" },
};

interface VirtualizedTableLogoAndNameProps extends StackProps {
  name: string;
  logo?: string;
  url?: string;
  type?: ColorSchemeType;
  prefetch?: boolean;
}

const VirtualizedTableLogoAndName = ({
  logo,
  name,
  url,
  type,
  prefetch = false,
  ...props
}: VirtualizedTableLogoAndNameProps) => {
  const { color, icon } = getColorAndIcon(url, type);

  return (
    <HStack
      as={url ? NextLink : undefined}
      href={url}
      target="_blank"
      prefetch={url ? prefetch : undefined}
      mb={1}
      _last={{ mb: 0 }}
      align="center"
      fontWeight="medium"
      outline="none"
      rounded="md"
      minW={0}
      {...(url && linkStyles)}
      {...props}
    >
      <Center
        h="25px"
        w="25px"
        rounded="md"
        overflow="hidden"
        bg={logo ? "white" : `${color}.100`}
        position="relative"
        flex="0 0 25px"
      >
        {logo ? (
          <ChakraImage
            src={logo}
            alt={`${name} Logo`}
            width="25px"
            height="25px"
          />
        ) : (
          <Icon as={icon} h={4} w={4} color={`${color}.500`} />
        )}
      </Center>

      <Box overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
        {name}
      </Box>
    </HStack>
  );
};

export default VirtualizedTableLogoAndName;
